import React, { useCallback, useEffect, useRef, useState } from "react";
import { Col, Divider, Form, InputNumber, Row, Select, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { ClockCircleOutlined, CloudUploadOutlined, InfoCircleOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Card } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { BACKUPS_ENDPOINT, FTP_ENDPOINT } from "../constants";

const { Text } = Typography;

// Hangfire takes standard five-field cron. These cover what an operator normally wants;
// anything else can still be typed in.
const CRON_PRESETS = [
  { value: "0 * * * *", label: "Every hour" },
  { value: "0 */6 * * *", label: "Every 6 hours" },
  { value: "0 3 * * *", label: "Every day at 03:00 UTC" },
  { value: "0 3 * * 0", label: "Every Sunday at 03:00 UTC" },
  { value: "0 3 1 * *", label: "First day of the month at 03:00 UTC" },
];

/**
 * Automatic-backup policy: on/off, how often, how many copies to keep, and where each archive
 * is copied once it is written — an FTP target, the S3 bucket, or both.
 *
 * `refreshKey` changes whenever the FTP or S3 tab changes something, so the picker and the
 * bucket toggle below do not go stale while the operator is on the same page.
 */
const ScheduleCard = ({ onSaved, refreshKey }) => {
  const { mutate } = useMutations();
  // Stable handle for the load effect — see the note in BackupsPage.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [form] = Form.useForm();
  const [schedule, setSchedule] = useState(null);
  const [ftpConnections, setFtpConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const ftpEnabled = Form.useWatch("ftpEnabled", form);

  const load = useCallback(async () => {
    setLoading(true);
    await mutateRef.current("backupSchedule", {
      method: "get",
      endpoint: `${BACKUPS_ENDPOINT}/schedule`,
      onSuccess: (data) => {
        setSchedule(data);
        form.setFieldsValue({
          enabled: data?.enabled ?? false,
          cron: data?.cron ?? "0 3 * * *",
          retentionDays: data?.retentionDays ?? 30,
          maxBackups: data?.maxBackups ?? 30,
          ftpEnabled: data?.ftpEnabled ?? false,
          ftpConnectionId: data?.ftpConnectionId ?? undefined,
          storageEnabled: data?.storageEnabled ?? false,
        });
      },
    });
    setLoading(false);
  }, [form]);

  const loadFtpConnections = useCallback(async () => {
    await mutateRef.current("scheduleFtpConnections", {
      method: "get",
      endpoint: FTP_ENDPOINT,
      onSuccess: (data) => setFtpConnections((data || []).filter((c) => c.isActive)),
    });
  }, []);

  // refreshKey is in here too: saving the S3 connection on its own tab flips
  // storageConfigured, which decides whether the toggle below can be turned on at all.
  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    loadFtpConnections();
  }, [loadFtpConnections, refreshKey]);

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);

    await mutate("saveBackupSchedule", {
      method: "put",
      endpoint: `${BACKUPS_ENDPOINT}/schedule`,
      payload: {
        ...values,
        tables: schedule?.tables ?? [],
        ftpConnectionId: values.ftpEnabled ? values.ftpConnectionId ?? null : null,
      },
      onSuccess: (data) => {
        setSchedule(data);
        onSaved?.();
      },
    });

    setSaving(false);
  };

  return (
    <Card
      title={
        <Space size={8}>
          <ClockCircleOutlined />
          Automatic backups
        </Space>
      }
      extra={
        schedule?.lastRunAt ? (
          <Tooltip title={new Date(schedule.lastRunAt).toLocaleString()}>
            <Tag color={schedule.lastRunStatus?.startsWith("Failed") ? "error" : "success"}>
              Last run: {schedule.lastRunStatus || "—"}
            </Tag>
          </Tooltip>
        ) : (
          <Tag>Never run</Tag>
        )
      }
      loading={loading}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={5}>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>

          <Col xs={24} md={9}>
            <Form.Item
              name="cron"
              label={
                <Space size={6}>
                  Schedule (cron, UTC)
                  <Tooltip title="Five-field cron: minute hour day month weekday. Pick a preset or type your own.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
              }
              rules={[{ required: true, message: "A cron expression is required." }]}
            >
              <Select
                showSearch
                allowClear={false}
                options={CRON_PRESETS}
                // Anything the operator types is accepted as-is; the server validates it
                // when it registers the job and reports back if Hangfire rejects it.
                onSearch={(text) => form.setFieldValue("cron", text)}
                filterOption={false}
                notFoundContent={<Text type="secondary">Press enter to use a custom expression</Text>}
              />
            </Form.Item>
          </Col>

          <Col xs={12} md={5}>
            <Form.Item
              name="retentionDays"
              label="Keep for (days)"
              tooltip="0 disables age-based pruning."
            >
              <InputNumber min={0} max={3650} style={{ width: "100%" }} />
            </Form.Item>
          </Col>

          <Col xs={12} md={5}>
            <Form.Item
              name="maxBackups"
              label="Keep at most"
              tooltip="0 disables count-based pruning. Only scheduled backups are pruned — manual and safety copies are never removed automatically."
            >
              <InputNumber min={0} max={1000} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: "0 0 16px" }} orientation="left" orientationMargin={0}>
          <Space size={8}>
            <CloudUploadOutlined />
            <Text type="secondary">Off-site copy</Text>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} md={7}>
            <Form.Item
              name="ftpEnabled"
              label="Send to FTP"
              valuePropName="checked"
              tooltip="Every scheduled archive is uploaded once it has been written. The local copy is kept either way."
            >
              <Switch />
            </Form.Item>
          </Col>

          <Col xs={24} md={17}>
            <Form.Item
              name="ftpConnectionId"
              label="FTP connection"
              rules={[
                {
                  // Saving the toggle without a target would fail server-side anyway; catching
                  // it here keeps the operator from losing the rest of the form.
                  required: !!ftpEnabled,
                  message: "Pick the FTP connection to upload to.",
                },
              ]}
            >
              <Select
                allowClear
                disabled={!ftpEnabled}
                placeholder={
                  ftpConnections.length
                    ? "Pick a target"
                    : "Add one on the FTP connection tab first"
                }
                options={ftpConnections.map((c) => ({
                  value: c.ftpConnectionId,
                  label: `${c.name} — ${c.host}${c.remoteDirectory ? c.remoteDirectory : ""}`,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={7}>
            {/* There is no target to pick next to this one: the object store is a single
                shared connection, so the switch is the whole policy. */}
            <Form.Item
              name="storageEnabled"
              label="Copy to S3"
              valuePropName="checked"
              tooltip="Every scheduled archive is copied into the bucket once it has been written. The local copy is kept either way."
            >
              <Switch disabled={!schedule?.storageConfigured} />
            </Form.Item>
          </Col>

          <Col xs={24} md={17}>
            {schedule && !schedule.storageConfigured && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Set the connection up on the S3 storage tab first.
              </Text>
            )}
          </Col>
        </Row>

        <Button variant="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
          Save schedule
        </Button>
      </Form>
    </Card>
  );
};

export default ScheduleCard;
